package components

import androidx.compose.runtime.*
import api.TradeNewsApiClient
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.isActive
import models.NewsItem
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * ニュース詳細ページ。
 * 日本語タイトル・AI要約・翻訳文・元記事リンクを表示する。
 */
@Composable
fun NewsDetail(
    newsId: String,
    client: TradeNewsApiClient,
    onBack: () -> Unit,
) {
    var newsItem by remember { mutableStateOf<NewsItem?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var retryNonce by remember { mutableStateOf(0) }

    LaunchedEffect(newsId, retryNonce, client) {
        isLoading = true
        error = null
        try {
            newsItem = client.fetchNewsItem(newsId)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            newsItem = null
            error = buildNewsFetchErrorMessage(e)
            console.error("Failed to fetch news item", e)
        } finally {
            if (currentCoroutineContext().isActive) {
                isLoading = false
            }
        }
    }

    Div(attrs = {
        classes("news-detail")
    }) {
        Button(attrs = {
            classes("back-link")
            onClick { onBack() }
        }) {
            Text("← 一覧に戻る")
        }

        when {
            isLoading -> {
                Div(attrs = {
                    classes("loading-container")
                }) {
                    Div(attrs = {
                        classes("loading-spinner")
                    })
                    P { Text("記事を読み込んでいます...") }
                }
            }
            error != null -> {
                Div(attrs = {
                    classes("error-container")
                }) {
                    H3(attrs = {
                        classes("error-title")
                    }) {
                        Text("記事を取得できなかった")
                    }
                    P(attrs = {
                        classes("error-message")
                    }) {
                        Text(error!!)
                    }
                    Button(attrs = {
                        classes("retry-button")
                        onClick { retryNonce += 1 }
                    }) {
                        Text("再試行する")
                    }
                }
            }
            newsItem == null -> {
                Div(attrs = {
                    classes("empty-container")
                }) {
                    P { Text("記事が見つからなかった。削除されたか、URLが間違っている可能性がある。") }
                }
            }
            else -> {
                NewsDetailContent(newsItem!!)
            }
        }
    }
}

@Composable
private fun NewsDetailContent(newsItem: NewsItem) {
    Article(attrs = {
        classes("news-detail-card")
    }) {
        Div(attrs = {
            classes("news-detail-meta")
        }) {
            Span(attrs = {
                classes("news-card-category", "category-${newsItem.category.lowercase()}")
            }) {
                Text(newsItem.category)
            }
            Span(attrs = {
                classes("news-detail-date")
            }) {
                Text(formatDate(newsItem.publishedAt))
            }
            Span(attrs = {
                classes("news-detail-source")
            }) {
                Text("Source: ${newsItem.source}")
            }
        }

        H2(attrs = {
            classes("news-detail-title")
        }) {
            Text(stripHtmlTags(newsItem.titleJa ?: newsItem.title))
        }

        NewsDetailSummarySection(newsItem)

        val description = newsItem.descriptionJa ?: newsItem.description
        if (description != null) {
            Section(attrs = {
                classes("news-detail-section")
            }) {
                H3(attrs = {
                    classes("news-detail-section-title")
                }) {
                    Text("概要")
                }
                P(attrs = {
                    classes("news-detail-description")
                }) {
                    Text(stripHtmlTags(description))
                }
            }
        }

        // javascript: 等の危険スキームを排除し、http/https のみリンクとして描画する
        val sourceLink = newsItem.link.takeIf(::isSafeExternalUrl)
        if (sourceLink != null) {
            A(href = sourceLink, attrs = {
                classes("news-detail-source-link")
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
            }) {
                Text("元記事を読む ↗")
            }
        }
    }
}

@Composable
private fun NewsDetailSummarySection(newsItem: NewsItem) {
    Section(attrs = {
        classes("news-detail-section")
    }) {
        H3(attrs = {
            classes("news-detail-section-title")
        }) {
            Text("AI要約 🤖")
        }

        val summary = newsItem.summaryJa
        if (summary != null && summary.isNotBlank()) {
            summary.split("\n\n").forEach { paragraph ->
                if (paragraph.isNotBlank()) {
                    P(attrs = {
                        classes("news-detail-summary-paragraph")
                    }) {
                        Text(paragraph.trim())
                    }
                }
            }
        } else {
            P(attrs = {
                classes("news-detail-summary-placeholder")
            }) {
                Text(buildSummaryPlaceholderMessage(newsItem.summaryStatus))
            }
        }
    }
}

/** 要約が無い場合のステータス別メッセージ */
fun buildSummaryPlaceholderMessage(summaryStatus: String): String {
    return when (summaryStatus) {
        "failed" -> "この記事は要約を生成できなかった。元記事を直接確認してくれ。"
        else -> "AI要約は準備中。生成され次第ここに表示される。"
    }
}

/** 外部リンクとして安全なスキーム（http/https）のみ許可する */
fun isSafeExternalUrl(url: String): Boolean {
    val normalized = url.trim().lowercase()
    return normalized.startsWith("https://") || normalized.startsWith("http://")
}

object NewsDetailStyles : StyleSheet() {
    init {
        ".news-detail" style {
            marginTop(1.5.em)
            maxWidth(800.px)
            property("margin-left", "auto")
            property("margin-right", "auto")
        }

        ".back-link" style {
            backgroundColor(Color.transparent)
            property("border", "none")
            color(Color("#60a5fa"))
            fontSize(1.em)
            fontWeight(600)
            cursor("pointer")
            padding(0.5.em, 0.px)
            marginBottom(1.em)
            fontFamily("inherit")
        }

        ".back-link:hover" style {
            color(Color("#93c5fd"))
        }

        ".news-detail-card" style {
            backgroundColor(rgba(30, 41, 59, 0.6))
            borderRadius(20.px)
            padding(2.em)
            property("border", "1px solid rgba(255,255,255,0.08)")
            property("backdrop-filter", "blur(16px)")
            property("box-shadow", "0 4px 24px rgba(0,0,0,0.25)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(1.25.em)
        }

        ".news-detail-meta" style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(1.em)
            flexWrap(FlexWrap.Wrap)
            fontSize(0.9.em)
            color(Color("#94a3b8"))
        }

        ".news-detail-source" style {
            fontStyle("italic")
        }

        ".news-detail-title" style {
            fontSize(1.6.em)
            fontWeight(700)
            lineHeight(2.2.cssRem)
            color(Color("#f1f5f9"))
            margin(0.px)
        }

        ".news-detail-section" style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(0.75.em)
        }

        ".news-detail-section-title" style {
            fontSize(1.05.em)
            fontWeight(700)
            color(Color("#60a5fa"))
            margin(0.px)
            property("letter-spacing", "0.03em")
        }

        ".news-detail-summary-paragraph" style {
            color(Color("#e2e8f0"))
            lineHeight(1.9.cssRem)
            margin(0.px)
        }

        ".news-detail-summary-placeholder" style {
            color(Color("#94a3b8"))
            fontStyle("italic")
            margin(0.px)
        }

        ".news-detail-description" style {
            color(Color("#94a3b8"))
            lineHeight(1.8.cssRem)
            margin(0.px)
        }

        ".news-detail-source-link" style {
            alignSelf("flex-start")
            property("background", "linear-gradient(135deg, #3b82f6, #8b5cf6)")
            color(Color.white)
            textDecoration("none")
            borderRadius(999.px)
            padding(0.7.em, 1.4.em)
            fontWeight(700)
            fontSize(0.95.em)
        }

        ".news-detail-source-link:hover" style {
            opacity(0.9)
        }

        media("(max-width: 600px)") {
            ".news-detail-card" style {
                padding(1.25.em)
            }

            ".news-detail-title" style {
                fontSize(1.3.em)
                lineHeight(1.9.cssRem)
            }
        }
    }
}
